'use client';

import { useEffect, useState } from 'react';
import PageLayout from '@organisms/PageLayout/PageLayout';
import Tooltip from '@molecules/Tooltip/Tooltip';
import { useTranslations } from 'next-intl';
import MultiSearchResult from '@organisms/MultiSearchComponent/MultiSearchResult';
import { trackSearchWords } from '@helpers/analyticsHelpers/trackMultiSearch';
import useResponsive from '@hooks/useResponsive';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Spinner from '@molecules/Spinner/Spinner';
import {
  SearchResultWrapper,
  StyledButton,
  StyledButtonWrapper,
  StyledMultiSearchWrapper,
  StyledTextArea,
  StyledTextAreaWrapper,
  StyledTitle,
} from './MultiSearchComponent.styles';

export enum MULTI_SEARCH {
  TEXTAREA_HEIGHT_MIN = 100,
  AMOUNT_OF_BEAMS_PER_PAGE = 5,
  AMOUNT_OF_SLIDES_FIRST_LOAD = 20,
}

const MultiSearchComponent = () => {
  const t = useTranslations('multiSearch');
  const [searchValue, setSearchValue] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(true);
  const [textAreaHeight, setTextAreaHeight] = useState(MULTI_SEARCH.TEXTAREA_HEIGHT_MIN);
  const [searchWordsArray, setSearchWordsArray] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { isMobile } = useResponsive();

  const textAreaOnChangeHandler = (e: any) => {
    setSearchValue(e?.currentTarget?.value);
    setTextAreaHeight(e?.target?.scrollHeight);
  };

  const onSubmitHandler = async () => {
    setCurrentPage(1);
    const searchWords = searchValue.split(/, +|\n+/);
    setSearchWordsArray(searchWords.filter(Boolean));
    trackSearchWords(searchWords.join());
  };

  useEffect(() => {
    if (localStorage.getItem('hideMultiSearchTooltip') === 'true') setTooltipOpen(false);
    else localStorage.setItem('hideMultiSearchTooltip', 'true');
  }, []);

  const hasNextPage = currentPage * MULTI_SEARCH.AMOUNT_OF_BEAMS_PER_PAGE < searchWordsArray.length;
  const onLoadMore = () => {
    setCurrentPage(currentPage + 1);
  };

  // Infinite scroll hook
  const [infiniteRef] = useInfiniteScroll({
    loading: false,
    hasNextPage,
    onLoadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <PageLayout noMobilePadding>
      <StyledMultiSearchWrapper>
        <StyledTitle type="body">{t('title')}</StyledTitle>

        <StyledTextAreaWrapper>
          <StyledTextArea
            resize={false}
            name="search"
            label={t('textArea.label')}
            labelTooltip={t('textArea.tooltip')}
            value={searchValue}
            onChange={textAreaOnChangeHandler}
            $height={textAreaHeight}
          />
          {tooltipOpen && (
            <Tooltip variant="medium" position="bottom" align="center" onCloseHandler={() => setTooltipOpen(false)}>
              {t('tooltip')}
            </Tooltip>
          )}
        </StyledTextAreaWrapper>

        <StyledButtonWrapper>
          {searchValue && (
            <StyledButton
              onClick={() => {
                setSearchValue('');
                setTextAreaHeight(MULTI_SEARCH.TEXTAREA_HEIGHT_MIN);
              }}
              variant="secondary"
            >
              {t('button.clear')}
            </StyledButton>
          )}
          <StyledButton disabled={searchValue.length === 0} onClick={onSubmitHandler}>
            {isMobile ? t('button.searchMobile') : t('button.search')}
          </StyledButton>
        </StyledButtonWrapper>
      </StyledMultiSearchWrapper>

      {searchWordsArray.length > 0 && (
        <>
          <SearchResultWrapper>
            {searchWordsArray
              .slice(0, currentPage * MULTI_SEARCH.AMOUNT_OF_BEAMS_PER_PAGE)
              .map((searchWord: any, index) => {
                return (
                  <MultiSearchResult
                    searchWord={searchWord}
                    searchWordsArray={searchWordsArray}
                    setSearchWordsArray={setSearchWordsArray}
                    key={searchWord}
                    index={index}
                  />
                );
              })}
          </SearchResultWrapper>
          {hasNextPage && <Spinner size={32} ref={infiniteRef} />}
        </>
      )}
    </PageLayout>
  );
};

export default MultiSearchComponent;
